import { Injectable } from '@angular/core';
import {
  AnalyzeGraphicComposant,
  AnalyzeGraphicDtoInterface,
  AssertEmptyComposantAttenduInterface,
  Composant,
  ComposantAttendu,
  DuplicateAndMoveInterface,
  Fichier,
  SocieteComposant,
  Valeur
} from '@get/api-interfaces';
import {
  ComposantActions,
  ComposantAttenduGeneratedActions,
  ComposantGeneratedActions,
  ValeurFichierGeneratedActions,
  ValeurGeneratedActions
} from '@get/store/actions';
import { ComposantApiService } from '@get/store/api-services';
import { AppState } from '@get/store/configs/reducers';
import { ComposantRelationsIds } from '@get/store/ids-interfaces';
import { catchApiActions } from '@get/utils';
import { Actions } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Observable, tap } from 'rxjs';
import { GeneratedComposantService } from './composant-generated.service';
import { saveAs } from 'file-saver';

@Injectable({
  providedIn: 'root'
})
export class ComposantService extends GeneratedComposantService {
  constructor(store$: Store<AppState>, actions$: Actions, private readonly composantApiService: ComposantApiService) {
    super(store$, actions$);
  }

  public duplicateOneComposant(
    idComposant: number,
    ids: ComposantRelationsIds = {},
    getResult = false,
    params?: { idCampagne?: number }
  ): void | Observable<Composant> {
    this.store$.dispatch(ComposantActions.duplicateOneComposant({ idComposant, ids, params }));
    if (getResult) {
      return catchApiActions(
        this.actions$,
        ComposantGeneratedActions.normalizeManyComposantsAfterUpsert,
        ComposantGeneratedActions.composantsFailure,
        true
      );
    }
  }

  public getManyComposantsPerType(params: any = {}, getResult?: boolean): void | Observable<Composant[]> {
    this.store$.dispatch(ComposantActions.getManyComposantsPerType({ params }));
    if (getResult) {
      return catchApiActions(
        this.actions$,
        ComposantGeneratedActions.normalizeManyComposantsAfterUpsert,
        ComposantGeneratedActions.composantsFailure
      );
    }
  }

  public getCountComposantCaracteristiques(params: AnalyzeGraphicDtoInterface): Promise<AnalyzeGraphicComposant> {
    return this.composantApiService.getCountComposantCaracteristiques(params);
  }

  public downloadOneFichier(fichier: Fichier): void {
    this.composantApiService
      .downloadOneFichier(fichier.idFichier)
      .pipe(tap(blob => saveAs(blob, fichier.originalName)))
      .subscribe();
  }

  public moveOneComposant(
    composant: Partial<Composant>,
    oldIdEspace: number,
    getResult?: boolean
  ): void | Observable<Composant> {
    this.store$.dispatch(ComposantActions.moveOneComposant({ composant, oldIdEspace }));
    if (getResult) {
      return catchApiActions(
        this.actions$,
        ComposantGeneratedActions.upsertManyComposantsSuccess,
        ComposantGeneratedActions.composantsFailure,
        true
      );
    }
  }

  public upsertOneComposantModifyEspace(
    composant: Partial<Composant>,
    oldIdEspace: number,
    getResult?: boolean
  ): void | Observable<Composant> {
    this.store$.dispatch(ComposantActions.upsertOneComposantModifyEspace({ composant, oldIdEspace }));
    if (getResult) {
      return catchApiActions(
        this.actions$,
        ComposantGeneratedActions.upsertManyComposantsSuccess,
        ComposantGeneratedActions.composantsFailure,
        true
      );
    }
  }

  public upsertOneValeurReturnComposant(
    valeur: Partial<Valeur>,
    getResult = false,
    idCampagne?: number
  ): void | Observable<Composant> {
    this.store$.dispatch(ComposantActions.upsertOneValeurAndReturnComposant({ valeur, params: { idCampagne } }));
    if (getResult) {
      return catchApiActions(
        this.actions$,
        ComposantGeneratedActions.normalizeManyComposantsAfterUpsert,
        ComposantGeneratedActions.composantsFailure,
        true
      );
    }
  }

  public upsertManyValeursReturnComposant(
    valeurs: Partial<Valeur>[],
    getResult = false,
    idCampagne?: number
  ): void | Observable<Composant> {
    this.store$.dispatch(ComposantActions.upsertManyValeursAndReturnComposants({ valeurs, params: { idCampagne } }));
    if (getResult) {
      return catchApiActions(
        this.actions$,
        ComposantGeneratedActions.normalizeManyComposantsAfterUpsert,
        ComposantGeneratedActions.composantsFailure,
        true
      );
    }
  }

  public updateOneComposantAndValeurs(
    composant: Partial<Composant>,
    getResult?: boolean
  ): void | Observable<{ composant: Composant; composantAttendus: ComposantAttendu[] }> {
    this.store$.dispatch(ComposantActions.updateOneComposantAndValeurs({ composant }));
    if (getResult) {
      return catchApiActions(
        this.actions$,
        ValeurGeneratedActions.normalizeManyValeursAfterUpsert,
        ValeurGeneratedActions.valeursFailure,
        true
      );
    }
  }

  public assertEmptyComposantAttendu(
    params: AssertEmptyComposantAttenduInterface,
    getResult?: boolean
  ): void | Observable<ComposantAttendu[]> {
    this.store$.dispatch(ComposantActions.assertEmptyComposantAttendu({ params }));
    if (getResult) {
      return catchApiActions(
        this.actions$,
        ComposantAttenduGeneratedActions.normalizeManyComposantAttendusAfterUpsert,
        ComposantAttenduGeneratedActions.composantAttendusFailure,
        true
      );
    }
  }

  public assertEmptyTree(
    params: AssertEmptyComposantAttenduInterface,
    getResult?: boolean
  ): void | Observable<ComposantAttendu[]> {
    this.store$.dispatch(ComposantActions.assertEmptyTree({ params }));
    if (getResult) {
      return catchApiActions(
        this.actions$,
        ComposantAttenduGeneratedActions.normalizeManyComposantAttendusAfterUpsert,
        ComposantAttenduGeneratedActions.composantAttendusFailure,
        true
      );
    }
  }

  public uploadManyImages(files: File[], queryParams: { idOrganisation?: number } = {}): Observable<Fichier[]> {
    return this.composantApiService.uploadManyImages(files, queryParams);
  }

  public uploadMany(files: File[], queryParams: { idOrganisation?: number } = {}): Observable<Fichier[]> {
    return this.composantApiService.uploadMany(files, queryParams);
  }

  public deleteOneComposantWithComposantAttendu(idComposant: number, getResult?: boolean): void | Observable<number> {
    this.store$.dispatch(ComposantActions.deleteOneComposantWithComposantAttendu({ idComposant }));
    if (getResult) {
      return catchApiActions(
        this.actions$,
        ComposantGeneratedActions.deleteOneComposantSuccess,
        ComposantGeneratedActions.composantsFailure
      );
    }
  }

  public deleteComposantsBySociete(idSociete: number, getResult?: boolean): void | Observable<number> {
    this.store$.dispatch(ComposantActions.deleteComposantsBySociete({ idSociete }));
    if (getResult) {
      return catchApiActions(
        this.actions$,
        ComposantActions.deleteComposantsBySocieteSuccess,
        ComposantGeneratedActions.composantsFailure
      );
    }
  }

  public deleteComposantsBySocieteComposant(
    idSocieteComposant: number,
    getResult?: boolean
  ): void | Observable<number> {
    this.store$.dispatch(ComposantActions.deleteComposantsBySocieteComposant({ idSocieteComposant }));
    if (getResult) {
      return catchApiActions(
        this.actions$,
        ComposantActions.deleteComposantsBySocieteComposantSuccess,
        ComposantGeneratedActions.composantsFailure
      );
    }
  }

  public getComposantsBySociete(idSociete: number): Promise<SocieteComposant[]> {
    return this.composantApiService.getComposantsBySociete(idSociete);
  }

  public createManyComposants(composants: Partial<Composant>[], getResult?: boolean): void | Observable<Composant> {
    this.store$.dispatch(ComposantActions.createManyComposants({ composants }));
    if (getResult) {
      return catchApiActions(
        this.actions$,
        ComposantGeneratedActions.normalizeManyComposantsAfterUpsert,
        ComposantGeneratedActions.composantsFailure,
        true
      );
    }
  }

  public deleteOneValeurFichier(idValeurFichier: number, getResult?: boolean): void | Observable<number> {
    this.store$.dispatch(ComposantActions.deleteOneValeurFichier({ idValeurFichier }));
    if (getResult) {
      return catchApiActions(
        this.actions$,
        ValeurFichierGeneratedActions.deleteOneValeurFichierSuccess,
        ValeurFichierGeneratedActions.valeurFichiersFailure
      );
    }
  }
}
